import React, { useCallback, useEffect, useState } from 'react';
import classes from '~/app/common/scss/section.module.scss';
import List from './List';
import Button from '~/app/common/Button';
import AddMaterialDialog from './Add';
import { useStoreActions, useStoreState } from '~/store';
import useTranslation from '~/app/hooks/useTranslation';
import useAccessControl from '~/app/hooks/useAccessControl';
import { DEPARTMENT_PERMISSION_GROUPS } from '~/accessControl/permissionGroups';
import { PartLinkTypes } from '~/app/data/statuses/issue';
import { getMaterialStoreKey, isMaterialsEditable } from './helper';
import Icon from '~/app/common/Icon';

export interface MaterialsProps {
  type: PartLinkTypes
}

export type MaterialsComponent = React.FC<MaterialsProps>;

const Materials: MaterialsComponent = ({ type }) => {
  const basePath = getMaterialStoreKey(type);
  const fetchMaterialsAction = useStoreActions(actions => actions[basePath].fetchMaterials);
  const setTempMaterialsAction = useStoreActions(actions => actions[basePath].setTempMaterials);
  const netWorkStatus = useStoreState(state => state.app.offline.status);
  const deptPrefix = useStoreState(state => state[basePath].data.departmentPrefix);
  const id = useStoreState(state => state[basePath].data.id);
  const status = useStoreState(state => state[basePath].data.status);
  const materials = useStoreState(state => state[basePath].data.materials ?? []);
  const [isOpenAddForm, setIsOpenAddForm] = useState(false);
  const [expanded, setExpanded] = useState(true);

  const { t } = useTranslation('parts');
  const { checkPermission } = useAccessControl();

  const addMaterialPermission = deptPrefix
    ? checkPermission(DEPARTMENT_PERMISSION_GROUPS.addParts(deptPrefix))
    : false;

  const canEdit = !netWorkStatus && isMaterialsEditable(type, status) && addMaterialPermission;

  const toggleDialog = useCallback((state: boolean) => (): void => {
    setTempMaterialsAction(materials);
    setIsOpenAddForm(state);
  }, [materials, setTempMaterialsAction]);

  useEffect(() => {
    if (!netWorkStatus) {
      fetchMaterialsAction(String(id));
    }
  }, [id, fetchMaterialsAction, netWorkStatus]);

  return (
    <>
      <div className={classes['container']}>
        <div className={classes['header']} onClick={() => setExpanded(!expanded)}>
          <span className={classes['headerText']}>{t('material')} ({materials?.length ?? 0})</span>
          {canEdit &&
            <Button
              className={classes['addButton']}
              onClick={(e) => {
                e.stopPropagation();
                toggleDialog(true)();
              }}
            >
              {t('add')}
            </Button>
          }
          <Icon name={expanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'} className={classes['chevron']} />
        </div>
        {expanded && (
          materials.length === 0 ? (
            <p className={classes.empty}>{t('noMaterials')}</p>
          ) : (
            <List type={type}/>
          )
        )}
        {canEdit && <AddMaterialDialog open={isOpenAddForm} onClose={toggleDialog(false)} type={type}/>}
        <hr className={classes['bottomLine']} />
      </div>
    </>
  );
};

export default Materials;
